import { Client, Realtime, type RealtimeSubscription } from 'appwrite';
import { appwriteConfig } from '../../config/environment';
import { account, appwriteClient } from './client';

export interface RealtimeMessage<T = Record<string, unknown>> {
  events: string[];
  payload: T;
}

export type Unsubscribe = () => void;
export type RealtimeErrorHandler = (error: unknown) => void;

interface RealtimeService {
  subscribe<T>(
    channels: string[],
    callback: (message: RealtimeMessage<T>) => void,
  ): Promise<Pick<RealtimeSubscription, 'unsubscribe'>>;
}

const realtime = new Realtime(appwriteClient);
let localRealtime: Promise<Realtime> | null = null;

export function isLocalOrigin(hostname: string): boolean {
  return hostname === 'localhost'
    || hostname === '127.0.0.1'
    || hostname === '::1'
    || hostname.endsWith('.localhost');
}

export async function createLocalRealtime(
  issueJwt: () => Promise<{ jwt: string }> = () => account.createJWT({ duration: 3600 }),
  build: (jwt: string) => Realtime = (jwt) => {
    const client = new Client()
      .setEndpoint(appwriteConfig.endpoint)
      .setProject(appwriteConfig.projectId)
      .setJWT(jwt);
    return new Realtime(client);
  },
): Promise<Realtime> {
  const { jwt } = await issueJwt();
  return build(jwt);
}

/**
 * En localhost Appwrite conserva la sesión mediante su cookie de respaldo en
 * las llamadas HTTP. Un WebSocket no puede mandar ese encabezado, así que se
 * autentica con un JWT efímero de la sesión actual. En producción se mantiene
 * la cookie segura normal y no se crea ninguna credencial adicional.
 */
export function realtimeForOrigin(hostname: string): Promise<Realtime> {
  if (!isLocalOrigin(hostname)) return Promise.resolve(realtime);

  localRealtime ??= createLocalRealtime().catch((caught) => {
    // Un JWT fallido no debe quedar cacheado toda la visita: al recuperar la
    // sesión, la siguiente suscripción puede intentarlo nuevamente.
    localRealtime = null;
    throw caught;
  });
  return localRealtime;
}

/** Canal de todas las filas de una tabla de Appwrite TablesDB. */
export function tableRowsChannel(databaseId: string, tableId: string): string {
  return `tablesdb.${databaseId}.tables.${tableId}.rows`;
}

/** Adapta la suscripción asíncrona del SDK a la limpieza de un efecto React. */
export function createRowsSubscriber(service: RealtimeService) {
  return function subscribe<T = Record<string, unknown>>(
    channels: string[],
    onMessage: (message: RealtimeMessage<T>) => void,
    onError?: RealtimeErrorHandler,
  ): Unsubscribe {
    const subscription = service.subscribe<T>(channels, onMessage);
    void subscription.catch((caught: unknown) => onError?.(caught));

    return () => {
      // Si el componente se desmontó mientras Appwrite todavía conectaba, se
      // cierra apenas la promesa termina de abrir la suscripción.
      void subscription
        .then((active) => active.unsubscribe())
        .catch(() => undefined);
    };
  };
}

/**
 * Escucha filas de TablesDB.
 *
 * Chat y notificaciones comparten una sola instancia y un WebSocket por
 * pestaña.
 */
export function subscribeToRows<T = Record<string, unknown>>(
  channels: string[],
  onMessage: (message: RealtimeMessage<T>) => void,
  onError?: RealtimeErrorHandler,
): Unsubscribe {
  const subscription = realtimeForOrigin(window.location.hostname)
    .then((service) => service.subscribe<T>(channels, onMessage));
  void subscription.catch((caught: unknown) => onError?.(caught));

  return () => {
    void subscription
      .then((active) => active.unsubscribe())
      .catch(() => undefined);
  };
}
